import { useMemo, useState } from 'react';
import { Palette, Replace, ChevronDown, ChevronRight } from 'lucide-react';
import { extractSvgColors, replaceSvgColor } from '../../lib/svgColorUtils';
import type { CanvasElement, SvgElement } from '../../types';

interface MultiSelectColorEditorProps {
  elements: CanvasElement[];
  onUpdateElements: (updates: Array<{ id: string; changes: Partial<CanvasElement> }>) => void;
}

interface AggregatedColor {
  hex: string;
  label: string;
  elementIds: string[];
  hasSvgElements: boolean;
  totalCount: number;
}

export default function MultiSelectColorEditor({ elements, onUpdateElements }: MultiSelectColorEditorProps) {
  const [expanded, setExpanded] = useState(true);
  const [replacingColor, setReplacingColor] = useState<string | null>(null);

  const aggregatedColors = useMemo(() => {
    const colorMap = new Map<string, AggregatedColor>();

    for (const el of elements) {
      if (el.type === 'svg') {
        const svgColors = extractSvgColors((el as SvgElement).svgMarkup);
        for (const sc of svgColors) {
          const existing = colorMap.get(sc.hex);
          if (existing) {
            if (!existing.elementIds.includes(el.id)) existing.elementIds.push(el.id);
            existing.hasSvgElements = true;
            existing.totalCount += sc.count;
          } else {
            colorMap.set(sc.hex, {
              hex: sc.hex,
              label: sc.label,
              elementIds: [el.id],
              hasSvgElements: true,
              totalCount: sc.count,
            });
          }
        }
      } else {
        const fill = (el as unknown as Record<string, unknown>).fill as string | undefined;
        const stroke = (el as unknown as Record<string, unknown>).stroke as string | undefined;
        for (const color of [fill, stroke]) {
          if (!color || color === 'none' || color === 'transparent') continue;
          const hex = color.startsWith('#') ? color.toLowerCase() : color;
          const existing = colorMap.get(hex);
          if (existing) {
            if (!existing.elementIds.includes(el.id)) existing.elementIds.push(el.id);
            existing.totalCount++;
          } else {
            colorMap.set(hex, {
              hex,
              label: hex.toUpperCase(),
              elementIds: [el.id],
              hasSvgElements: false,
              totalCount: 1,
            });
          }
        }
      }
    }

    return Array.from(colorMap.values()).sort((a, b) => b.totalCount - a.totalCount);
  }, [elements]);

  const handleReplaceColor = (oldHex: string, newHex: string) => {
    const colorInfo = aggregatedColors.find((c) => c.hex === oldHex);
    if (!colorInfo) return;

    const updates: Array<{ id: string; changes: Partial<CanvasElement> }> = [];

    for (const elementId of colorInfo.elementIds) {
      const el = elements.find((e) => e.id === elementId);
      if (!el) continue;

      if (el.type === 'svg') {
        const newMarkup = replaceSvgColor((el as SvgElement).svgMarkup, oldHex, newHex);
        updates.push({ id: el.id, changes: { svgMarkup: newMarkup } as Partial<CanvasElement> });
      } else {
        const changes: Record<string, unknown> = {};
        if ((el as unknown as Record<string, unknown>).fill?.toString().toLowerCase() === oldHex) {
          changes.fill = newHex;
        }
        if ((el as unknown as Record<string, unknown>).stroke?.toString().toLowerCase() === oldHex) {
          changes.stroke = newHex;
        }
        if (Object.keys(changes).length > 0) {
          updates.push({ id: el.id, changes: changes as Partial<CanvasElement> });
        }
      }
    }

    if (updates.length > 0) {
      onUpdateElements(updates);
    }
    setReplacingColor(newHex);
  };

  if (aggregatedColors.length === 0) return null;

  return (
    <div className="border-b border-white/[0.04]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-white/[0.02] transition-colors"
      >
        {expanded ? <ChevronDown size={13} className="text-white/30" /> : <ChevronRight size={13} className="text-white/30" />}
        <Palette size={13} className="text-cyan-400" />
        <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Colors in Selection</span>
        <span className="text-[10px] text-white/25 ml-auto">{aggregatedColors.length}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-1.5">
          <p className="text-[9px] text-white/25 mb-2">
            Click a color to replace it across {elements.length} selected elements
          </p>

          {aggregatedColors.map((color) => (
            <div key={color.hex} className="flex items-center gap-2">
              <button
                onClick={() => setReplacingColor(replacingColor === color.hex ? null : color.hex)}
                className="w-6 h-6 rounded border border-white/20 hover:border-white/40 transition-colors flex-shrink-0"
                style={{ backgroundColor: color.hex }}
                title={`${color.label} -- used by ${color.elementIds.length} element(s)`}
              />

              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-white/60 truncate">{color.label}</div>
                <div className="text-[9px] text-white/25">
                  {color.elementIds.length} element{color.elementIds.length !== 1 ? 's' : ''} · {color.totalCount}x
                </div>
              </div>

              {color.hasSvgElements && (
                <span className="text-[8px] px-1 py-0.5 rounded bg-cyan-500/15 text-cyan-400">SVG</span>
              )}
            </div>
          ))}

          {replacingColor && (
            <div className="mt-2 p-2 bg-white/[0.03] rounded-lg border border-white/[0.06]">
              <div className="flex items-center gap-1.5 mb-2">
                <Replace size={11} className="text-white/30" />
                <span className="text-[9px] text-white/30">
                  Replace {aggregatedColors.find((c) => c.hex === replacingColor)?.label || replacingColor}
                </span>
              </div>
              <input
                type="color"
                value={replacingColor}
                onChange={(e) => handleReplaceColor(replacingColor, e.target.value)}
                className="w-full h-8 rounded border border-white/10 cursor-pointer bg-transparent"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
